/**
 * Connection state, the sync queue and the download, surfaced honestly.
 *
 * What works offline and what does not is STATED, not discovered. Someone in
 * a field needs to know whether the thing they are looking at is current
 * before they walk twenty minutes to a stage.
 *
 * **Opening a rave downloads it.** The bundle is already on the device by the
 * time this mounts (`EditionProvider` stored it); this fetches the photos and
 * the map the bundle's asset manifest names, quietly, in the background — on
 * the first open and again whenever the bundle version changes. Attending
 * lands here too (the directory goes straight into the rave, and storage is
 * per ORIGIN, so a download started on `app.` would be invisible to the
 * rave's own subdomain). Mobile data included (2026-10-07); only with
 * Save-Data on, or a 2G link, it waits for Wi-Fi. **No buttons at all** (the
 * user, 2026-10-27: "every download and offline is happening automatically"):
 * no "Download for offline", no "Try again" — a stopped download retries on
 * its own. There is no download card either (2026-09-28); the one line that
 * says where things stand is `OfflineFacts`, on the rave's Info screen.
 * **Signed out, none of it**: online only — nothing downloaded, no stored
 * copy read (`offline/bundle.ts`), and no line about offline.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiUrl } from '@/lib/tenant';
import { useT } from '@/i18n';
import { isReachable, onSyncStatus, requestSync, syncStatus, type ConnectionState } from './sync';
import { downloadFinished, downloadable, precacheAssets } from './bundle';
import { requestPersistence } from './db';
import { isDataSaving, onConnectionChange } from './net';
import { overForOffline } from './pastRaves';
import type { EditionBundle } from '@myrave/shared';
import './OfflineProvider.css';
import '@/app/SignInStrip.css';
import { isFramed } from '@/lib/framed';
import { endpoints } from '@/lib/api';
import { signedInHere } from '@/lib/signOut';
import { knownAttending, onAttending } from '@/lib/attendance';

/**
 * How long after the page settles the background download waits. The first
 * paint's own images (the cover, the stage banners) need the connection first;
 * on festival wifi a few seconds are nothing beside a 23 MB download.
 */
export const DOWNLOAD_GRACE_MS = 3_000;

/**
 * A download that stopped is tried again by itself after this, doubling to
 * `DOWNLOAD_RETRY_MAX_MS` — not only on the browser's `online` event, which a
 * phone on a signal that is ON but useless never sends (the user, 2026-10-14).
 */
export const DOWNLOAD_RETRY_MS = 60_000;
const DOWNLOAD_RETRY_MAX_MS = 10 * 60_000;

/**
 * Run `fn` once the page has loaded, the browser is idle — or, where there is no
 * `requestIdleCallback` (Safari), a moment after `load` — and `graceMs` more
 * have passed. Returns a cancel.
 *
 * Idle alone was not enough: by the time a rave's bundle arrived the page was
 * long "loaded" and idle came at once, so the download still began beside the
 * cover (step 14).
 */
export function afterPageSettles(fn: () => void, fallbackMs = 1_500, graceMs = 0): () => void {
  let cancelled = false;
  let cancelStep = () => {};
  const run = () => {
    if (cancelled) return;
    if (graceMs <= 0) { fn(); return; }
    const id = window.setTimeout(() => { if (!cancelled) fn(); }, graceMs);
    cancelStep = () => window.clearTimeout(id);
  };
  const idle = () => {
    if (cancelled) return;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 5_000 });
      cancelStep = () => window.cancelIdleCallback(id);
    } else {
      const id = window.setTimeout(run, fallbackMs);
      cancelStep = () => window.clearTimeout(id);
    }
  };
  if (document.readyState === 'complete') idle();
  else {
    window.addEventListener('load', idle, { once: true });
    cancelStep = () => window.removeEventListener('load', idle);
  }
  return () => { cancelled = true; cancelStep(); };
}

export interface DownloadState {
  done: number;
  total: number;
  bytes: number;
  finished: boolean;
  /**
   * The last attempt ended with assets still missing (the signal dropped, a
   * photo would not load). Resumed on the next open and when the network
   * comes back; until then nobody is told the map works offline.
   */
  stalled?: boolean;
}

interface OfflineState {
  connection: ConnectionState;
  /** Null until a download has been started (or found finished) for this bundle version. */
  download: DownloadState | null;
  /** True when the download is waiting for Wi-Fi (Save-Data / cellular). */
  deferred: boolean;
  pending: number;
  /** Changes the server refused — kept on this phone, not in the account. */
  rejected: number;
  startDownload: () => void;
  syncNow: () => void;
}

const Ctx = createContext<OfflineState>({
  connection: 'online', download: null, deferred: false, pending: 0, rejected: 0,
  startDownload: () => {}, syncNow: () => {},
});

export function useOffline(): OfflineState {
  return useContext(Ctx);
}

export function OfflineProvider({ bundle, children }: { bundle: EditionBundle; children: ReactNode }) {
  const editionId = bundle.edition.id;
  const editionEnds = bundle.edition.endsAt;
  const [reachable, setReachable] = useState(true);
  const reachableNow = useRef(true);
  /** A download that was due while offline (or on a limited link): it starts when the probe answers. */
  const waitingForNetwork = useRef(false);
  const startRef = useRef<() => void>(() => {});
  const [status, setStatus] = useState(() => syncStatus(editionId));
  const [download, setDownload] = useState<DownloadState | null>(null);
  const [deferred, setDeferred] = useState(false);
  // Attended after it opened — by Attend or by saving a set: the download runs again (`lib/attendance.ts`).
  const [attendedTick, setAttendedTick] = useState(0);
  useEffect(() => onAttending((id) => { if (id === editionId) setAttendedTick((n) => n + 1); }), [editionId]);

  useEffect(() => onSyncStatus(editionId, setStatus), [editionId]);

  // Drain on launch: whatever was queued in a field last night goes now.
  useEffect(() => { void requestSync(editionId); }, [editionId]);

  // `navigator.onLine` only says an interface is up. Festival wifi with no
  // upstream reports online and reaches nothing, so probe the API — and when
  // it comes back, drain the queue without being asked.
  useEffect(() => {
    let cancelled = false;
    let was = true;
    /*
     * A LIMITED link is OFFLINE (the user, 2026-10-16: "limited connection —
     * use offline mode"): an API that does not answer inside the 2.5 s probe
     * is no connection, however slowly it might answer later. The app runs on
     * what the phone holds, and the photo download waits for a real one.
     */
    const check = async () => {
      const ok = await isReachable(apiUrl());
      if (cancelled) return;
      reachableNow.current = ok;
      setReachable(ok);
      if (ok && !was) void requestSync(editionId);
      if (ok && waitingForNetwork.current) { waitingForNetwork.current = false; startRef.current(); }
      was = ok;
    };
    const online = () => { void requestSync(editionId); void check(); };
    void check();
    const id = setInterval(check, 30_000);
    window.addEventListener('online', online);
    window.addEventListener('offline', check);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', check);
    };
  }, [editionId]);

  const syncNow = useCallback(() => { void requestSync(editionId); }, [editionId]);

  /* ------------------------------------------------------------ download */

  const running = useRef<{ version: string; controller: AbortController } | null>(null);

  const startDownload = useCallback(() => {
    if (running.current?.version === bundle.version) return;
    running.current?.controller.abort();
    const controller = new AbortController();
    running.current = { version: bundle.version, controller };
    setDeferred(false);
    // Ask the browser not to evict the very lineup someone downloaded
    // precisely because they knew they would have no signal.
    void requestPersistence();
    const total = downloadable(bundle).length;
    setDownload((d) => ({ done: d?.done ?? 0, total, bytes: d?.bytes ?? 0, finished: total === 0 }));
    void precacheAssets(bundle, (done, all, bytes) => {
      if (!controller.signal.aborted) setDownload({ done, total: all, bytes, finished: done >= all });
    }, controller.signal)
      .then((out) => {
        if (controller.signal.aborted) return;
        setDownload({ done: out.done, total: out.total, bytes: out.bytes, finished: out.complete, stalled: !out.complete });
      })
      .catch(() => {
        if (!controller.signal.aborted) setDownload((d) => (d ? { ...d, stalled: true } : d));
      })
      .finally(() => {
        // Let a later call (the network back, "Download") start it again.
        if (running.current?.controller === controller) running.current = null;
      });
  }, [bundle]);

  /** The automatic download, only on a connection the probe calls one. */
  const startWhenOnline = useCallback(() => {
    if (reachableNow.current) startDownload();
    else waitingForNetwork.current = true;
  }, [startDownload]);
  startRef.current = startDownload;

  // An interrupted download carries on when the network comes back — not only
  // at the next open — and, since the signal may never have "gone" (on but
  // useless), on a clock too: a minute, doubling to ten.
  const retries = useRef(0);
  useEffect(() => {
    if (download?.finished) retries.current = 0;
    if (!download?.stalled) return undefined;
    const resume = () => { if (!isDataSaving()) startWhenOnline(); };
    const wait = Math.min(DOWNLOAD_RETRY_MS * 2 ** retries.current, DOWNLOAD_RETRY_MAX_MS);
    const timer = window.setTimeout(() => { retries.current++; resume(); }, wait);
    window.addEventListener('online', resume);
    return () => { window.clearTimeout(timer); window.removeEventListener('online', resume); };
  }, [download?.stalled, download?.finished, startWhenOnline]);

  useEffect(() => {
    let cancelled = false;
    let unlisten = () => {};
    void downloadFinished(editionId, bundle.version).then(async (done) => {
      if (cancelled) return;
      if (done) {
        setDownload({ done: done.total, total: done.total, bytes: done.bytes, finished: true });
        return;
      }
      // The wizard's preview frame keeps nothing for offline — and signed out is online only
      // (the user, 2026-10-27: "if the user is not signed in it's only online mode").
      if (isFramed() || !signedInHere()) return;
      // A rave over by more than two days is online only: its photos and map are not kept (2026-11-02).
      if (overForOffline(editionEnds)) return;
      // Only a rave this account ATTENDS is downloaded (the user, 2026-10-16:
      // "download the attended raves only") — an admin opening a rave they run
      // but do not attend keeps nothing. The list is the network's, else the
      // last one this account got (offline included).
      if (!(await attendsRave(editionId)) || cancelled) return;
      if (!isDataSaving()) {
        // Not during the first paint: the download is 23 MB for OZORA, and
        // started at once it took the connection from the page itself — the
        // programme PDF (3.6 MB) beat the cover on screen, and Lighthouse put
        // the largest paint at 27 s (step 14, 2026-10-05).
        unlisten = afterPageSettles(startWhenOnline, 1_500, DOWNLOAD_GRACE_MS);
        return;
      }
      // Metered: wait for Wi-Fi, or for somebody to press Download.
      setDeferred(true);
      unlisten = onConnectionChange(() => {
        // A real connection: the data-saving strip goes at once (the user, 2026-10-27), the
        // download starts — or waits for the server to answer, with nothing on screen to press.
        if (!isDataSaving()) { unlisten(); setDeferred(false); startWhenOnline(); }
      });
    });
    return () => { cancelled = true; unlisten(); };
  }, [editionId, editionEnds, bundle.version, startWhenOnline, attendedTick]);

  useEffect(() => () => { running.current?.controller.abort(); running.current = null; }, []);

  const connection: ConnectionState = status.syncing ? 'syncing' : reachable ? 'online' : 'offline';

  return (
    <Ctx.Provider value={{ connection, download, deferred, pending: status.pending, rejected: status.rejected ?? 0, startDownload, syncNow }}>
      {children}
    </Ctx.Provider>
  );
}

/**
 * The banner. Present when it matters, absent when it does not.
 *
 * **"Offline — showing your downloaded lineup" is gone**, at the user's
 * instruction (2026-09-24). The app is offline-first, so reading the stored
 * copy is the normal case, not an incident. Saves waiting to sync went the
 * same way on 2026-10-07. What survives is changes the server refused.
 *
 * The schedule's `StoredCopyNotice` (visitor/Schedule.tsx) is driven by the
 * bundle request actually failing, not by the probe, and says WHEN the stored
 * copy was synced.
 */
/**
 * What the connection line says, if anything: only that the server REFUSED
 * some changes for good — the one thing a person might act on.
 *
 * Saves waiting to go up are not said any more (the user, 2026-10-07: "remove
 * this — 1 save waiting to sync"). The queue drains by itself whenever there
 * is a network and a session; a line about it was noise on every phone in a
 * field. Before that (2026-10-05) "Syncing" was already limited to something
 * actually going up.
 */
export function connectionLine(
  _connection: ConnectionState, _pending: number, rejected: number,
): 'rejected' | null {
  return rejected > 0 ? 'rejected' : null;
}

export function ConnectionBanner() {
  const { connection, pending, rejected } = useOffline();
  const tr = useT();
  if (!connectionLine(connection, pending, rejected)) return null;

  return (
    <div className="offline-provider-banner">
      {/*
       * The server refused them for good (a 4xx). They are still on this
       * phone and still drawn; they are just not in the account.
       */}
      {rejected === 1
        ? tr('{count} change was not accepted by the server — it stays on this phone only', { count: rejected })
        : tr('{count} changes were not accepted by the server — they stay on this phone only', { count: rejected })}
    </div>
  );
}

/**
 * A strip under the header while the download waits on data saving (the user, 2026-10-27:
 * "you are on data saving — connect to Wi-Fi or cellular data to download the data to use
 * offline mode"). No button: the download starts by itself on a real connection. Only for
 * somebody signed in — signed out there is no offline to wait for. It sits and leaves with
 * the header, as the sign-in strip does (`SignInStrip.css`).
 */
export function DataSavingStrip() {
  const { download, deferred } = useOffline();
  const tr = useT();
  if (!deferred || download || !signedInHere()) return null;
  return (
    <div className="sign-in-strip data-saving-strip" role="status">
      <span className="sign-in-strip-text">{tr("You're on data saving — connect to Wi-Fi or mobile data to download this rave for offline use.")}</span>
    </div>
  );
}

/**
 * What works without a signal, and what does not (plan.md §21) — one short
 * line, not a card. While the photos and the map are downloading it says so,
 * with the count; while the download waits for Wi-Fi it offers to start now.
 */
export function OfflineFacts() {
  const { download, deferred } = useOffline();
  const tr = useT();
  // Signed out is online only: nothing here works offline, so nothing to say about it.
  if (!signedInHere()) return null;

  if (download && !download.finished && download.stalled) {
    return (
      <p className="offline-provider-facts-line">
        {tr('Some photos and the map are not on this phone yet — the download carries on when there is a signal.')}{' '}
        <span className="offline-provider-muted" dir="ltr">{download.done} / {download.total}</span>
      </p>
    );
  }

  if (download && !download.finished) {
    return (
      <p className="offline-provider-facts-line">
        {tr('Downloading photos and the map')}{' '}
        <span className="offline-provider-muted" dir="ltr">{download.done} / {download.total}</span>
      </p>
    );
  }

  if (deferred && !download) {
    return (
      <p className="offline-provider-facts-line">
        {tr('Photos and the map download on Wi-Fi.')}
      </p>
    );
  }

  /*
   * The map is promised only once it is DOWNLOADED. Before the first check
   * lands, or while the download waits for Wi-Fi (with it already started),
   * the lineup is what this phone is known to hold.
   */
  if (!download?.finished) {
    return (
      <p className="offline-provider-facts-line">
        <strong className="offline-provider-works">{tr('Works offline')}</strong>{' '}
        {tr('The full lineup, your saved acts, and the countdown.')}{' '}
        <strong className="offline-provider-needs">{tr('Needs a signal')}</strong>{' '}
        {tr('Photos and the map until they have downloaded, audio previews, and syncing your saves.')}
      </p>
    );
  }

  return (
    <p className="offline-provider-facts-line">
      <strong className="offline-provider-works">{tr('Works offline')}</strong>{' '}
      {tr('The full lineup, the map, your saved acts, and the countdown.')}{' '}
      <strong className="offline-provider-needs">{tr('Needs a signal')}</strong>{' '}
      {tr('Audio previews you have not played yet, and syncing your saves to your account.')}
    </p>
  );
}

const ATTENDS_WAIT_MS = 10_000;

/** Does this account attend the rave? From `/me/festivals` (stored-first offline); no answer is no. */
export async function attendsRave(editionId: string): Promise<boolean> {
  // Attended since the page opened — by Attend or by saving a set (`lib/attendance.ts`).
  if (knownAttending(editionId)) return true;
  try {
    // Never waited on for ever: a useless signal with no stored list is no.
    const mine = await Promise.race([
      endpoints.myFestivals(),
      new Promise<never>((_, no) => setTimeout(() => no(new Error('no answer')), ATTENDS_WAIT_MS)),
    ]);
    return (mine.festivals ?? []).some((f) => f.editionId === editionId && f.attending !== false);
  } catch {
    return false;
  }
}
